import { api, API_BASE, getApiToken } from "./api";

export const THEME_PRESETS = [
  "default",
  "sunset",
  "ocean",
  "mono",
  "forest",
  "berry",
  "midnight",
  "candy",
] as const;
export type ThemePreset = (typeof THEME_PRESETS)[number];

export type PageMode = "curated" | "full_shop";

export type FooterMode = "inherit" | "custom" | "none";

export type HeroSlide = {
  image_url: string;
  headline?: string;
  subtitle?: string;
  cta_label?: string;
  cta_url?: string;
  accent_color?: string;
};

export type PromoPill = {
  label: string;
  code?: string;
  hint?: string;
  url?: string;
};

export type PageProduct = {
  id: string;
  wc_product_id: number;
  position: number;
  custom_headline: string | null;
  snapshot_name: string;
  snapshot_permalink: string;
  snapshot_price: string | null;
  snapshot_image_url: string | null;
  click_count: number;
  is_featured: boolean;
  discount_code: string | null;
  discount_hint: string | null;
};

export type FaqItem = { q: string; a: string };

export type CustomBlock =
  | { type: "text"; props: { markdown: string } }
  | { type: "video"; props: { url: string; caption?: string } }
  | { type: "spacer"; props: { size?: "sm" | "md" | "lg" } }
  | {
      type: "bundle";
      props: { title: string; product_ids: string[]; cta_label?: string };
    }
  | { type: "faq"; props: { title?: string; items: FaqItem[] } }
  | {
      type: "testimonial";
      props: { quote: string; author: string; role?: string; image_url?: string };
    }
  | {
      type: "image";
      props: { url: string; alt?: string; caption?: string; link_url?: string };
    }
  | {
      type: "section_header";
      props: { title: string; emoji?: string; subtitle?: string };
    };

export type AgentPage = {
  id: string;
  agent_id: string;
  headline: string | null;
  subtitle: string | null;
  accent_color: string | null;
  theme_preset: string;
  avatar_url: string | null;
  cover_url: string | null;
  logo_url: string | null;
  brand_primary: string | null;
  brand_secondary: string | null;
  instagram_url: string | null;
  tiktok_url: string | null;
  whatsapp_number: string | null;
  whatsapp_message: string | null;
  footer_mode: FooterMode;
  legal_imprint: string | null;
  legal_terms: string | null;
  legal_privacy: string | null;
  custom_domain: string | null;
  custom_domain_verified: boolean;
  hero_slides: HeroSlide[];
  promo_strip: PromoPill[];
  is_published: boolean;
  view_count: number;
  page_mode: string;
  custom_blocks: CustomBlock[];
  products: PageProduct[];
  public_url: string;
  public_path: string;
};

export type PageUpdate = {
  headline?: string | null;
  subtitle?: string | null;
  accent_color?: string | null;
  brand_primary?: string | null;
  brand_secondary?: string | null;
  theme_preset?: ThemePreset | null;
  avatar_url?: string | null;
  cover_url?: string | null;
  instagram_url?: string | null;
  tiktok_url?: string | null;
  whatsapp_number?: string | null;
  whatsapp_message?: string | null;
  footer_mode?: FooterMode | null;
  legal_imprint?: string | null;
  legal_terms?: string | null;
  legal_privacy?: string | null;
  hero_slides?: HeroSlide[] | null;
  promo_strip?: PromoPill[] | null;
  is_published?: boolean;
  page_mode?: PageMode | null;
};

export const getMyPage = () => api.get<AgentPage>("/agent/page");

export const updateMyPage = (payload: PageUpdate) =>
  api.put<AgentPage>("/agent/page", payload);

export const addPageProduct = (wc_product_id: number, custom_headline?: string) =>
  api.post<AgentPage>("/agent/page/products", { wc_product_id, custom_headline });

export const removePageProduct = (productRowId: string) =>
  api.delete<AgentPage>(`/agent/page/products/${productRowId}`);

export const updatePageProduct = (
  productRowId: string,
  payload: {
    custom_headline?: string | null;
    is_featured?: boolean;
    discount_code?: string | null;
    discount_hint?: string | null;
  },
) => api.put<AgentPage>(`/agent/page/products/${productRowId}`, payload);

export const updatePageBlocks = (blocks: CustomBlock[]) =>
  api.put<AgentPage>("/agent/page/blocks", { blocks });

// Page-Templates

export type PageTemplate = {
  id: string;
  name: string;
  description: string;
  icon: string;
  theme_preset: string;
  page_mode: string;
  block_count: number;
};

export const listPageTemplates = () =>
  api.get<PageTemplate[]>("/agent/page/templates");

export const applyPageTemplate = (templateId: string) =>
  api.post<AgentPage>(`/agent/page/templates/${templateId}/apply`);

export const reorderPageProducts = (product_ids: string[]) =>
  api.put<AgentPage>("/agent/page/products/order", { product_ids });

export const refreshProductSnapshot = (productRowId: string) =>
  api.post<AgentPage>(`/agent/page/products/${productRowId}/refresh-snapshot`);

// Image-Uploads

export type UploadResponse = { url: string };

export async function uploadPageImage(
  kind: "avatar" | "cover" | "logo" | "block" | "testimonial",
  file: File,
): Promise<UploadResponse> {
  const form = new FormData();
  form.append("file", file);
  const token = getApiToken() || "";
  const res = await fetch(`${API_BASE}/agent/page/upload/${kind}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Upload fehlgeschlagen (${res.status})`);
  }
  return res.json();
}

export const removePageImage = (kind: "avatar" | "cover" | "logo") =>
  api.delete<AgentPage>(`/agent/page/upload/${kind}`);

// Public

export type PublicProduct = {
  id: string;
  wc_product_id: number;
  name: string;
  permalink: string;
  price: string | null;
  image_url: string | null;
  custom_headline: string | null;
  is_featured?: boolean;
  type?: string | null;
  categories?: string[];
  available_variations?: number;
  total_variations?: number;
  stock_quantity?: number | null;
  low_stock_amount?: number | null;
  total_sales?: number;
  discount_code?: string | null;
  discount_hint?: string | null;
};

export type PublicPage = {
  agent_code: string;
  agent_first_name: string;
  agent_last_name: string;
  headline: string | null;
  subtitle: string | null;
  accent_color: string | null;
  theme_preset: string;
  avatar_url: string | null;
  cover_url: string | null;
  logo_url: string | null;
  brand_primary: string | null;
  brand_secondary: string | null;
  instagram_url: string | null;
  tiktok_url: string | null;
  whatsapp_number: string | null;
  whatsapp_message: string | null;
  footer_mode: FooterMode;
  legal_imprint: string | null;
  legal_terms: string | null;
  legal_privacy: string | null;
  hero_slides: HeroSlide[];
  promo_strip: PromoPill[];
  page_mode: string;
  custom_blocks: CustomBlock[];
  products: PublicProduct[];
  categories: string[];
  is_b2b_locked: boolean;
  register_url: string | null;
};

export const getPublicPage = (agentCode: string) =>
  api.get<PublicPage>(`/public/page/${encodeURIComponent(agentCode)}`);

// Live-Suche
export type SearchHit = {
  id: string;
  name: string;
  permalink: string;
  image_url: string | null;
  price: string | null;
  categories: string[];
};

export type SearchResponse = { query: string; hits: SearchHit[] };

export const searchPublicProducts = (agentCode: string, q: string, limit = 8) =>
  api.get<SearchResponse>(
    `/public/page/${encodeURIComponent(agentCode)}/search?q=${encodeURIComponent(q)}&limit=${limit}`,
  );

// Produkt-Detail-Page (PDP)

export type PdpImage = { url: string; alt: string };
export type PdpAttribute = { name: string; slug: string; options: string[] };
export type PdpVariation = {
  id: number;
  price: string | null;
  regular_price?: string | null;
  sale_price?: string | null;
  in_stock: boolean;
  stock_quantity: number | null;
  attributes: Record<string, string | null>;
  image: string | null;
};

export type PublicProductDetail = {
  id: number;
  agent_code: string;
  name: string;
  slug: string;
  permalink: string;
  type: string;
  price: string | null;
  regular_price: string | null;
  sale_price: string | null;
  on_sale: boolean;
  description_html: string;
  short_description_html: string;
  sku: string | null;
  stock_status: string | null;
  stock_quantity: number | null;
  total_sales: number;
  images: PdpImage[];
  categories: string[];
  attributes: PdpAttribute[];
  variations: PdpVariation[];
  average_rating: string | null;
  rating_count: number;
  is_b2b_locked: boolean;
  register_url: string | null;
};

export const getPublicProduct = (agentCode: string, productId: number | string) =>
  api.get<PublicProductDetail>(
    `/public/page/${encodeURIComponent(agentCode)}/product/${productId}`,
  );

export type RelatedProductsResponse = { base_id: number; items: PublicProduct[] };

export const getRelatedProducts = (agentCode: string, productId: number | string, limit = 6) =>
  api.get<RelatedProductsResponse>(
    `/public/page/${encodeURIComponent(agentCode)}/product/${productId}/related?limit=${limit}`,
  );

// Cross-Domain Cart-Token

export type CartTokenItem = {
  wc_product_id: number;
  qty: number;
  variation_id?: number | null;
};

export type CreateCartTokenResponse = {
  token: string;
  expires_at: string;
  redirect_url: string;
};

export const createCartToken = (agentCode: string, items: CartTokenItem[]) =>
  api.post<CreateCartTokenResponse>(
    `/public/page/${encodeURIComponent(agentCode)}/cart-token`,
    { items },
  );

// Custom-Domain

export type CustomDomainStatus = {
  domain: string | null;
  verified: boolean;
  target_a_record: string;
  target_cname: string;
  resolved_ips?: string[];
  message?: string;
};

export const getCustomDomain = () =>
  api.get<CustomDomainStatus>("/agent/page/domain");

export const setCustomDomain = (domain: string | null) =>
  api.put<CustomDomainStatus>("/agent/page/domain", { domain });

export const verifyCustomDomain = () =>
  api.post<CustomDomainStatus>("/agent/page/domain/verify");

// Domain → agent_code Resolve (public, no auth)
export type ResolveDomainResponse = { agent_code: string; custom_domain: string };
export const resolvePublicDomain = (host: string) =>
  api.get<ResolveDomainResponse>(`/public/resolve-domain?host=${encodeURIComponent(host)}`);

export function trackProductClick(agentCode: string, productRowId: string): void {
  // Fire-and-forget — Beacon API überlebt Page-Navigation
  const url = `${API_BASE}/public/page/${encodeURIComponent(agentCode)}/click/${productRowId}`;
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url);
      return;
    }
  } catch {
    /* fallthrough */
  }
  fetch(url, { method: "POST", keepalive: true }).catch(() => {});
}

export function absoluteUploadUrl(url: string | null): string | null {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  if (url.startsWith("/uploads/")) return `${API_BASE}${url}`;
  return url;
}

// --- Feature-Flags (public, kein Auth) --------------------------------------

export type PublicFeatures = {
  agent_shop_enabled: boolean;
  sharing_templates_enabled: boolean;
};

export const getPublicFeatures = () =>
  api.get<PublicFeatures>("/public/features");
